/**
 * OvernightFinancingTabContent.tsx
 * Overnight financing tab content for the fee configuration editor.
 *
 * Renders the overnight financing form (edit mode) or read-only display (inherited mode).
 * Supports benchmark selection, admin spread, triple charges, long/short rate differentiation,
 * minimum fees, calculation notes, and verification section.
 *
 * This is a controlled component — the parent (FeeConfigurationEditor) owns the
 * overnight state and passes it via props. Changes are communicated via onChange.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ShieldCheck, Trash2, NotepadText, Settings2 } from 'lucide-react';

// Workspace packages
import {
  Select,
  SelectItem,
  NumberInput,
  Input,
  TextArea,
  Switch,
  Button,
  Chip,
  Card,
  CardHeader,
  CardBody,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@enfomentis/ui';
import type { Selection } from '@react-types/shared';

// Internal absolute imports (type-only)
import type {
  OvernightBenchmark,
  OvernightFrequency,
} from '@/types/shared/FeeEnums';
import type { OvernightFinancingStructure } from '@/types/shared/feeStructures/OvernightFinancingStructure';

// Internal absolute imports
import {
  OVERNIGHT_BENCHMARK_DISPLAY_MAP,
  OVERNIGHT_FREQUENCY_DISPLAY_MAP,
  VERIFICATION_STATUS_DISPLAY_MAP,
} from '@/constants/feeEnums';
import { needsVerification } from '@/utils/feeEnums';
import { toDisplayValue, toStorageValue } from '@/utils/moneyHelpers';

// Relative imports
import type { OvernightFinancingTabContentProps, LoosePartial } from './feeEditorTypes';
import {
  VERIFICATION_STATUS_COLOR_MAP,
  formatFeeAmount,
  rescaleForPrecision,
} from './feeEditorUtils';

// =============================================================================
// Constants
// =============================================================================

/** Display labels for triple charge day dropdown */
const TRIPLE_CHARGE_DAY_DISPLAY_MAP: Readonly<Record<NonNullable<OvernightFinancingStructure['tripleChargeDay']>, string>> = {
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
};

/** Precision dropdown options (0–6 decimal places) */
const PRECISION_OPTIONS = [
  { value: 0, label: '0 — (1)' },
  { value: 1, label: '1 — (0.1)' },
  { value: 2, label: '2 — (0.01)' },
  { value: 3, label: '3 — (0.001)' },
  { value: 4, label: '4 — (0.0001)' },
  { value: 5, label: '5 — (0.00001)' },
  { value: 6, label: '6 — (0.000001)' },
] as const;

// =============================================================================
// Component
// =============================================================================

export const OvernightFinancingTabContent: React.FC<OvernightFinancingTabContentProps> = ({
  overnight,
  mode,
  accountCurrency,
  onChange,
  onRemove,
}) => {
  // ---------------------------------------------------------------------------
  // Change Helpers
  // ---------------------------------------------------------------------------

  const emitChange = useCallback(
    (partial: LoosePartial<typeof overnight>): void => {
      if (!onChange) return;
      const next: Record<string, unknown> = { ...overnight };
      for (const [key, value] of Object.entries(partial)) {
        if (value === undefined) {
          delete next[key];
        } else {
          next[key] = value;
        }
      }
      onChange(next as unknown as typeof overnight);
    },
    [overnight, onChange]
  );

  // ---------------------------------------------------------------------------
  // UI-Only Toggle: Use Different Long/Short Rates
  // ---------------------------------------------------------------------------

  const [useDifferentRates, setUseDifferentRates] = useState(
    () => overnight.longAdminSpread !== undefined || overnight.shortAdminSpread !== undefined
  );

  // Re-derive toggle when overnight prop changes (node switch, configure/remove, sync update)
  useEffect(() => {
    setUseDifferentRates(
      overnight.longAdminSpread !== undefined || overnight.shortAdminSpread !== undefined
    );
  }, [overnight.longAdminSpread, overnight.shortAdminSpread]);

  const handleDifferentRatesToggle = useCallback(
    (checked: boolean): void => {
      setUseDifferentRates(checked);
      if (checked) {
        // Prefill only missing long/short values from adminSpread (don't overwrite existing)
        const updates: LoosePartial<typeof overnight> = {};
        if (overnight.longAdminSpread === undefined) {
          updates.longAdminSpread = overnight.adminSpread;
          updates.longAdminSpreadPrecision = overnight.adminSpreadPrecision;
        }
        if (overnight.shortAdminSpread === undefined) {
          updates.shortAdminSpread = overnight.adminSpread;
          updates.shortAdminSpreadPrecision = overnight.adminSpreadPrecision;
        }
        if (Object.keys(updates).length > 0) {
          emitChange(updates);
        }
      } else {
        // Clear long/short fields
        emitChange({
          longAdminSpread: undefined,
          longAdminSpreadPrecision: undefined,
          shortAdminSpread: undefined,
          shortAdminSpreadPrecision: undefined,
        });
      }
    },
    [overnight, emitChange]
  );

  // ---------------------------------------------------------------------------
  // Select Handlers
  // ---------------------------------------------------------------------------

  const handleBenchmarkChange = useCallback(
    (keys: Selection): void => {
      if (keys === 'all' || !onChange) return;
      const newVal = Array.from(keys)[0] as OvernightBenchmark | undefined;
      if (!newVal || newVal === overnight.benchmark) return;
      // Clear customBenchmarkName when switching away from custom
      if (overnight.benchmark === 'custom' && newVal !== 'custom') {
        emitChange({ benchmark: newVal, customBenchmarkName: undefined });
      } else {
        emitChange({ benchmark: newVal });
      }
    },
    [overnight, onChange, emitChange]
  );

  const handleFrequencyChange = useCallback(
    (keys: Selection): void => {
      if (keys === 'all') return;
      const value = Array.from(keys)[0] as OvernightFrequency | undefined;
      if (value) emitChange({ frequency: value });
    },
    [emitChange]
  );

  const handleTripleChargeDayChange = useCallback(
    (keys: Selection): void => {
      if (keys === 'all') return;
      const value = Array.from(keys)[0] as NonNullable<OvernightFinancingStructure['tripleChargeDay']> | undefined;
      if (value) emitChange({ tripleChargeDay: value });
    },
    [emitChange]
  );

  // ---------------------------------------------------------------------------
  // Verify Handler
  // ---------------------------------------------------------------------------

  const handleVerify = useCallback((): void => {
    emitChange({
      verificationStatus: 'userVerified',
      lastVerifiedAt: new Date().toISOString(),
    });
  }, [emitChange]);

  // ---------------------------------------------------------------------------
  // Precision Handler (spread fields only — basis points)
  // ---------------------------------------------------------------------------

  const handlePrecisionChange = useCallback(
    (newPrecision: number): void => {
      const changes: LoosePartial<typeof overnight> = {};

      // Admin Spread — always present
      const oldAdminP = overnight.adminSpreadPrecision ?? 2;
      changes.adminSpreadPrecision = newPrecision;
      changes.adminSpread = rescaleForPrecision(overnight.adminSpread, oldAdminP, newPrecision) ?? 0;

      // Long Admin Spread — only when defined
      if (overnight.longAdminSpread !== undefined) {
        const oldLongP = overnight.longAdminSpreadPrecision ?? 2;
        changes.longAdminSpreadPrecision = newPrecision;
        changes.longAdminSpread = rescaleForPrecision(overnight.longAdminSpread, oldLongP, newPrecision);
      }

      // Short Admin Spread — only when defined
      if (overnight.shortAdminSpread !== undefined) {
        const oldShortP = overnight.shortAdminSpreadPrecision ?? 2;
        changes.shortAdminSpreadPrecision = newPrecision;
        changes.shortAdminSpread = rescaleForPrecision(overnight.shortAdminSpread, oldShortP, newPrecision);
      }

      // minFeePerPosition NOT touched — different unit (currency)

      emitChange(changes);
    },
    [overnight, emitChange]
  );

  // ---------------------------------------------------------------------------
  // Derived State
  // ---------------------------------------------------------------------------

  const canVerify =
    needsVerification(overnight.verificationStatus) ||
    overnight.verificationStatus === 'verified';

  /** Current spread precision for popover selectedKeys */
  const currentPrecision = overnight.adminSpreadPrecision ?? 2;

  /** Currency hint for currency-denominated fields; fallback when no currency set */
  const currencyHint = accountCurrency ?? 'Integer amount';
  /** Hint for basis-point fields */
  const bpsHint = 'Basis points';

  // ---------------------------------------------------------------------------
  // Inherited Mode (Read-Only Display)
  // ---------------------------------------------------------------------------

  if (mode === 'inherited') {
    return (
      <div className="flex flex-col gap-3 p-3">
        {/* Rate Configuration Card */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Rate Configuration
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            {/* Benchmark + Frequency — paired on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-medium text-text-secondary">Benchmark</span>
                <span className="text-base md:text-sm text-text-primary">
                  {OVERNIGHT_BENCHMARK_DISPLAY_MAP[overnight.benchmark]}
                  {overnight.benchmark === 'custom' && overnight.customBenchmarkName && (
                    <> — {overnight.customBenchmarkName}</>
                  )}
                </span>
              </div>
              <div className="flex flex-col gap-1 min-w-0">
                <span className="text-xs font-medium text-text-secondary">Frequency</span>
                <span className="text-base md:text-sm text-text-primary">
                  {OVERNIGHT_FREQUENCY_DISPLAY_MAP[overnight.frequency]}
                </span>
              </div>
            </div>

            {/* Admin Spread + Min Fee Per Position — paired on desktop */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className={`flex flex-col gap-1 min-w-0${overnight.minFeePerPosition === undefined ? ' md:col-span-2' : ''}`}>
                <span className="text-xs font-medium text-text-secondary">Admin Spread</span>
                <span className="text-base md:text-sm text-text-primary">
                  {formatFeeAmount(overnight.adminSpread, overnight.adminSpreadPrecision)} bps
                </span>
              </div>
              {overnight.minFeePerPosition !== undefined && (
                <div className="flex flex-col gap-1 min-w-0">
                  <span className="text-xs font-medium text-text-secondary">Min Fee Per Position</span>
                  <span className="text-base md:text-sm text-text-primary">
                    {formatFeeAmount(overnight.minFeePerPosition, overnight.minFeePerPositionPrecision)}
                  </span>
                </div>
              )}
            </div>

            {/* Calculation Notes */}
            {overnight.calculationNotes && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Calculation Notes</span>
                <span className="text-base md:text-sm text-text-primary">{overnight.calculationNotes}</span>
              </div>
            )}
          </CardBody>
        </Card>

        {/* Triple Charge Card */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Triple Charge
          </CardHeader>
          <CardBody className="px-1 pb-0">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-secondary">Triple Charges</span>
              <span className="text-base md:text-sm text-text-primary">
                {overnight.hasTripleCharges
                  ? `Yes — ${overnight.tripleChargeDay ? TRIPLE_CHARGE_DAY_DISPLAY_MAP[overnight.tripleChargeDay] ?? overnight.tripleChargeDay : 'Day not set'}`
                  : 'No'}
              </span>
            </div>
          </CardBody>
        </Card>

        {/* Long/Short Differentiation Card — conditional in inherited mode */}
        {(overnight.longAdminSpread !== undefined || overnight.shortAdminSpread !== undefined) && (
          <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
            <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
              Long/Short Rates
            </CardHeader>
            <CardBody className="px-1 pb-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {overnight.longAdminSpread !== undefined && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-text-secondary">Long Admin Spread</span>
                    <span className="text-base md:text-sm text-text-primary">
                      {formatFeeAmount(overnight.longAdminSpread, overnight.longAdminSpreadPrecision)} bps
                    </span>
                  </div>
                )}
                {overnight.shortAdminSpread !== undefined && (
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-text-secondary">Short Admin Spread</span>
                    <span className="text-base md:text-sm text-text-primary">
                      {formatFeeAmount(overnight.shortAdminSpread, overnight.shortAdminSpreadPrecision)} bps
                    </span>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        )}

        {/* Verification Card */}
        <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
          <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
            Verification
          </CardHeader>
          <CardBody className="px-1 pb-0 space-y-3">
            <div className="flex items-center gap-2">
              <Chip
                size="sm"
                variant="flat"
                color={VERIFICATION_STATUS_COLOR_MAP[overnight.verificationStatus]}
              >
                {VERIFICATION_STATUS_DISPLAY_MAP[overnight.verificationStatus]}
              </Chip>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-medium text-text-secondary">Last Verified</span>
              <span className="text-base md:text-sm text-text-primary">
                {overnight.lastVerifiedAt
                  ? new Date(overnight.lastVerifiedAt).toLocaleDateString()
                  : 'Not verified'}
              </span>
            </div>
            {overnight.verificationNotes && (
              <div className="flex flex-col gap-1">
                <span className="text-xs font-medium text-text-secondary">Verification Notes</span>
                <span className="text-base md:text-sm text-text-primary">{overnight.verificationNotes}</span>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Edit Mode
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col gap-3 p-3">
      {/* Rate Configuration Card */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span className="min-w-0 truncate">Rate Configuration</span>
            <div className="flex items-center gap-1 shrink-0">
              {/* Calculation Notes Popover */}
              <Popover placement="bottom-end">
                <PopoverTrigger>
                  <Button
                    size="sm"
                    variant="light"
                    color={overnight.calculationNotes ? 'primary' : 'default'}
                    startContent={<NotepadText size={16} />}
                  >
                    Notes
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-3">
                  <div className="w-72 flex flex-col gap-2">
                    <p className="text-xs font-medium text-text-secondary">
                      Calculation Notes
                    </p>
                    <TextArea
                      value={overnight.calculationNotes ?? ''}
                      onValueChange={(val) => emitChange({ calculationNotes: val || undefined })}
                      size="sm"
                      classNames={{ input: 'text-base md:text-sm' }}
                      placeholder="Broker-specific calculation methodology..."
                      minRows={3}
                      maxRows={8}
                    />
                  </div>
                </PopoverContent>
              </Popover>
              {/* Precision Popover */}
              {overnight.adminSpread !== undefined && (
                <Popover placement="bottom-end">
                  <PopoverTrigger>
                    <Button size="sm" variant="light" startContent={<Settings2 size={16} />}>
                      Precision
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-3">
                    <div className="w-52 flex flex-col gap-3">
                      <p className="text-xs font-medium text-text-secondary">
                        Spread Decimal Precision
                      </p>
                      <Select
                        label="Decimal Places"
                        labelPlacement="outside"
                        size="sm"
                        selectedKeys={[String(currentPrecision)]}
                        onSelectionChange={(keys) => {
                          if (keys === 'all') return;
                          const val = Array.from(keys)[0] as string | undefined;
                          if (val !== undefined) handlePrecisionChange(Number(val));
                        }}
                        classNames={{ value: 'text-base md:text-sm' }}
                      >
                        {PRECISION_OPTIONS.map((opt) => (
                          <SelectItem key={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </Select>
                    </div>
                  </PopoverContent>
                </Popover>
              )}
              {/* Remove Overnight Financing */}
              {onRemove && (
                <Button
                  size="sm"
                  variant="light"
                  color="danger"
                  isIconOnly
                  aria-label="Remove overnight financing"
                  onPress={onRemove}
                >
                  <Trash2 size={14} />
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3">
          {/* Benchmark + Frequency — paired on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Select
              label="Benchmark"
              labelPlacement="outside-top"
              className="min-w-0"
              selectedKeys={[overnight.benchmark]}
              onSelectionChange={handleBenchmarkChange}
              size="sm"
              classNames={{ value: 'text-base md:text-sm' }}
            >
              {(Object.entries(OVERNIGHT_BENCHMARK_DISPLAY_MAP) as [OvernightBenchmark, string][]).map(
                ([key, label]) => (
                  <SelectItem key={key}>{label}</SelectItem>
                )
              )}
            </Select>
            <Select
              label="Frequency"
              labelPlacement="outside-top"
              className="min-w-0"
              selectedKeys={[overnight.frequency]}
              onSelectionChange={handleFrequencyChange}
              size="sm"
              classNames={{ value: 'text-base md:text-sm' }}
            >
              {(Object.entries(OVERNIGHT_FREQUENCY_DISPLAY_MAP) as [OvernightFrequency, string][]).map(
                ([key, label]) => (
                  <SelectItem key={key}>{label}</SelectItem>
                )
              )}
            </Select>
          </div>

          {/* Custom Benchmark Name — shown only when benchmark is 'custom' */}
          {overnight.benchmark === 'custom' && (
            <Input
              label="Custom Benchmark Name"
              value={overnight.customBenchmarkName ?? ''}
              onValueChange={(val) => emitChange({ customBenchmarkName: val || undefined })}
              size="sm"
              isRequired
              isInvalid={!overnight.customBenchmarkName?.trim()}
              errorMessage={!overnight.customBenchmarkName?.trim() ? 'Benchmark name is required' : undefined}
              placeholder="Enter custom benchmark name"
              classNames={{ input: 'text-base md:text-sm' }}
            />
          )}

          {/* Admin Spread + Min Fee Per Position — paired on desktop */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <NumberInput
              label="Admin Spread"
              labelPlacement="outside"
              className="min-w-0"
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
              value={toDisplayValue(overnight.adminSpread, overnight.adminSpreadPrecision)}
              onValueChange={(val) => emitChange({ adminSpread: toStorageValue(val, overnight.adminSpreadPrecision) ?? 0 })}
              formatOptions={{ minimumFractionDigits: overnight.adminSpreadPrecision ?? 2, maximumFractionDigits: overnight.adminSpreadPrecision ?? 2 }}
              size="sm"
              hideStepper
              isWheelDisabled
              classNames={{ input: 'text-base md:text-sm text-right' }}
              placeholder="0"
            />
            <NumberInput
              label="Min Fee Per Position"
              labelPlacement="outside"
              className="min-w-0"
              endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{currencyHint}</span>}
              value={toDisplayValue(overnight.minFeePerPosition, overnight.minFeePerPositionPrecision)}
              onValueChange={(val) => {
                if (val === undefined) {
                  // Clear both value and precision to avoid stale hidden precision
                  emitChange({ minFeePerPosition: undefined, minFeePerPositionPrecision: undefined });
                } else {
                  const changes: LoosePartial<typeof overnight> = {
                    minFeePerPosition: toStorageValue(val, overnight.minFeePerPositionPrecision ?? 2),
                  };
                  if (overnight.minFeePerPositionPrecision === undefined) {
                    changes.minFeePerPositionPrecision = 2;
                  }
                  emitChange(changes);
                }
              }}
              formatOptions={{ minimumFractionDigits: 2, maximumFractionDigits: 2 }}
              size="sm"
              hideStepper
              isWheelDisabled
              classNames={{ input: 'text-base md:text-sm text-right' }}
              placeholder="Optional"
            />
          </div>
        </CardBody>
      </Card>

      {/* Triple Charge Card — switch in header, body conditional */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span>Triple Charge</span>
            <Switch
              isSelected={overnight.hasTripleCharges}
              onValueChange={(val) => {
                if (val) {
                  emitChange({ hasTripleCharges: true });
                } else {
                  emitChange({ hasTripleCharges: false, tripleChargeDay: undefined });
                }
              }}
              aria-label="Enable triple charges"
              size="sm"
            />
          </div>
        </CardHeader>
        {overnight.hasTripleCharges && (
          <CardBody className="px-1 pb-0 space-y-3">
            <Select
              label="Triple Charge Day"
              labelPlacement="outside-top"
              selectedKeys={overnight.tripleChargeDay ? [overnight.tripleChargeDay] : []}
              onSelectionChange={handleTripleChargeDayChange}
              size="sm"
              isRequired
              isInvalid={!overnight.tripleChargeDay}
              errorMessage={!overnight.tripleChargeDay ? 'Triple charge day is required' : undefined}
              classNames={{ value: 'text-base md:text-sm' }}
            >
              {Object.entries(TRIPLE_CHARGE_DAY_DISPLAY_MAP).map(([key, label]) => (
                <SelectItem key={key}>{label}</SelectItem>
              ))}
            </Select>
          </CardBody>
        )}
      </Card>

      {/* Long/Short Rates Card — switch in header, body conditional */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span>Long/Short Rates</span>
            <Switch
              isSelected={useDifferentRates}
              onValueChange={handleDifferentRatesToggle}
              aria-label="Use different long/short rates"
              size="sm"
            />
          </div>
        </CardHeader>
        {useDifferentRates && (
          <CardBody className="px-1 pb-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Long Admin Spread (precision hidden — controlled via Rate Configuration popover) */}
              <NumberInput
                label="Long Spread"
                labelPlacement="outside"
                className="min-w-0"
                endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
                value={toDisplayValue(overnight.longAdminSpread, overnight.longAdminSpreadPrecision)}
                onValueChange={(val) => emitChange({ longAdminSpread: toStorageValue(val, overnight.longAdminSpreadPrecision) })}
                formatOptions={{ minimumFractionDigits: overnight.longAdminSpreadPrecision ?? 2, maximumFractionDigits: overnight.longAdminSpreadPrecision ?? 2 }}
                size="sm"
                hideStepper
                isWheelDisabled
                classNames={{ input: 'text-base md:text-sm text-right' }}
                placeholder="0"
              />
              {/* Short Admin Spread (precision hidden — controlled via Rate Configuration popover) */}
              <NumberInput
                label="Short Spread"
                labelPlacement="outside"
                className="min-w-0"
                endContent={<span className="text-base md:text-sm text-text-tertiary whitespace-nowrap leading-none">{bpsHint}</span>}
                value={toDisplayValue(overnight.shortAdminSpread, overnight.shortAdminSpreadPrecision)}
                onValueChange={(val) => emitChange({ shortAdminSpread: toStorageValue(val, overnight.shortAdminSpreadPrecision) })}
                formatOptions={{ minimumFractionDigits: overnight.shortAdminSpreadPrecision ?? 2, maximumFractionDigits: overnight.shortAdminSpreadPrecision ?? 2 }}
                size="sm"
                hideStepper
                isWheelDisabled
                classNames={{ input: 'text-base md:text-sm text-right' }}
                placeholder="0"
              />
            </div>
          </CardBody>
        )}
      </Card>

      {/* Verification Card */}
      <Card radius="sm" shadow="none" className="p-1 border-1 border-default-200">
        <CardHeader className="px-1 pt-0 pb-2 text-sm font-semibold text-text-primary border-b-1 border-default-200">
          <div className="flex items-center justify-between w-full">
            <span>Verification</span>
            <Popover placement="bottom-end">
              <PopoverTrigger>
                <Button
                  size="sm"
                  variant="light"
                  color={overnight.verificationNotes ? 'primary' : 'default'}
                  startContent={<NotepadText size={16} />}
                >
                  Notes
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-3">
                <div className="w-72 flex flex-col gap-2">
                  <p className="text-xs font-medium text-text-secondary">
                    Verification Notes
                  </p>
                  <TextArea
                    value={overnight.verificationNotes ?? ''}
                    onValueChange={(val) => emitChange({ verificationNotes: val || undefined })}
                    size="sm"
                    classNames={{ input: 'text-base md:text-sm' }}
                    placeholder="Notes about fee verification or discrepancies..."
                    minRows={3}
                    maxRows={8}
                  />
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </CardHeader>
        <CardBody className="px-1 pb-0 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Chip
                size="sm"
                variant="flat"
                color={VERIFICATION_STATUS_COLOR_MAP[overnight.verificationStatus]}
              >
                {VERIFICATION_STATUS_DISPLAY_MAP[overnight.verificationStatus]}
              </Chip>
            </div>
            {canVerify && (
              <Button
                size="sm"
                variant="flat"
                color="success"
                startContent={<ShieldCheck size={14} />}
                onPress={handleVerify}
              >
                Verify
              </Button>
            )}
          </div>

          <p className="text-xs text-text-secondary">
            Last verified:{' '}
            {overnight.lastVerifiedAt
              ? new Date(overnight.lastVerifiedAt).toLocaleDateString()
              : 'Not verified'}
          </p>
        </CardBody>
      </Card>
    </div>
  );
};

export default OvernightFinancingTabContent;
